import { yupResolver } from "@hookform/resolvers/yup";
import { t } from "i18next";
import { useCallback, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import * as yup from "yup";

import { ApiResponseErrorType } from "~/api/apiResponseTypes.ts";
import { handleApiError } from "~/api/axiosUtils.ts";
import Button from "~/components/buttons/Button/Button.tsx";
import FormHasErrorsHint from "~/components/formElements/FormHasErrorsHint.tsx";
import FormSection, { FormSectionGroup } from "~/components/formElements/FormSection";
import SubmitButton from "~/components/formElements/SubmitButton.tsx";
import Textarea from "~/components/formElements/Textarea.tsx";
import SidebarBusyOverlay from "~/components/Sidebar/components/SidebarBusyOverlay.tsx";
import SidebarContent from "~/components/Sidebar/components/SidebarContent.tsx";
import SidebarErrorOverlay from "~/components/Sidebar/components/SidebarErrorOverlay.tsx";
import SidebarFooter from "~/components/Sidebar/components/SidebarFooter.tsx";
import SidebarHeader from "~/components/Sidebar/components/SidebarHeader.tsx";
import SidebarHeaderHeadline from "~/components/Sidebar/components/SidebarHeaderHeadline.tsx";
import { useFormIsSubmittable } from "~/hooks/form/useFormIsSubmittable.ts";
import { updateQuotaDetails } from "~/modules/quota/api/quota/quotaApiDispatchers.ts";
import type { QuotaWithRelations } from "~/modules/quota/api/quota/quotaTypes.ts";
import type { QuotaCoreDetailsFormData } from "~/modules/quota/components/formSections/QuotaCoreDetailsFormSection.tsx";
import QuotaCoreDetailsFormSection from "~/modules/quota/components/formSections/QuotaCoreDetailsFormSection.tsx";
import { getQuotaCoreDetailsSchemaFields, prepareUpdateQuotaDetailsPayload } from "~/modules/quota/utils/quotaFormUtils.ts";
import { parseDateString } from "~/utils/dateAndTimeUtils.ts";
import { preventSubmitOnEnter } from "~/utils/form/formUtils.ts";

export type UpdateQuotaDetailsFormData = QuotaCoreDetailsFormData & {
	preselectForTimeTracking: boolean;
	comment: string | null;
};

type Props = {
	onCancel: () => void;
	onSuccess: () => void;
	quota: QuotaWithRelations;
};

export const UpdateQuotaDetailsForm = ({ onCancel, onSuccess, quota }: Props) => {
	const [isBusy, setIsBusy] = useState(false);
	const [serverErrorMessage, setServerErrorMessage] = useState("");

	const schema = useMemo(() => yup.object({
		...getQuotaCoreDetailsSchemaFields(),
		preselectForTimeTracking: yup.boolean().required().default(false),
	}), []);

	const defaultValues = useMemo(() => {
		return {
			title: quota.title,
			managedBy: quota.managedBy ?? "",
			clientContactPerson: quota.clientContactPerson ?? "",
			usableFrom: quota.usableFrom ? parseDateString(quota.usableFrom) : null,
			usableUntil: quota.usableUntil ? parseDateString(quota.usableUntil) : null,
			preselectForTimeTracking: quota.preselectForTimeTracking,
			comment: quota.comment ?? "",
		};
	}, [quota]);

	const {
		handleSubmit,
		control,
		formState: { isDirty, isSubmitted, isValid },
	} = useForm<UpdateQuotaDetailsFormData>({
		defaultValues: defaultValues,
		resolver: yupResolver<UpdateQuotaDetailsFormData>(schema),
	});

	const formIsSubmittable = useFormIsSubmittable({
		isSubmitted,
		isDirty,
		isValid,
		isLoading: isBusy,
	});

	const onSubmit = useCallback(async (data: UpdateQuotaDetailsFormData) => {
		try {
			setIsBusy(true);
			await updateQuotaDetails(quota.companyId, quota.id, prepareUpdateQuotaDetailsPayload(data));
			onSuccess();
		} catch (error) {
			// The backend gives the definite answer, for example on a duplicate title in the company.
			const apiError = handleApiError(error);
			if (apiError.type === ApiResponseErrorType.VALIDATION) {
				setServerErrorMessage(Object.values(apiError.messages)
					.flat()
					.map((detail) => detail.message)
					.join("\n"));
			} else {
				setServerErrorMessage(t("form:unexpectedError"));
			}
			setIsBusy(false);
		}
	}, [onSuccess, quota.companyId, quota.id]);

	return (
		<form onSubmit={handleSubmit(onSubmit)}
			  onKeyDown={preventSubmitOnEnter}
			  className="auto-grid-sidebar"
		>
			<SidebarHeader>
				<SidebarHeaderHeadline>Kontingentdetails bearbeiten</SidebarHeaderHeadline>
			</SidebarHeader>
			<SidebarContent>
				{isBusy && <SidebarBusyOverlay />}
				{!!serverErrorMessage &&
					<SidebarErrorOverlay title="Update fehlgeschlagen">
						<span className="whitespace-pre-line">{serverErrorMessage}</span>
					</SidebarErrorOverlay>}
				<FormSectionGroup>
					<QuotaCoreDetailsFormSection control={control}
												 ignoreUserId={quota.managedBy ?? undefined}
												 showPreselectForTimeTracking />
					<FormSection title="Anmerkungen">
						<Textarea name="comment"
								  control={control} />
					</FormSection>
				</FormSectionGroup>
			</SidebarContent>
			<SidebarFooter>
				<FormHasErrorsHint show={isSubmitted && !isValid}
								   className="mr-2" />
				<SubmitButton busy={isBusy}
							  disabled={!formIsSubmittable}>
					{t("form:buttonTexts.update")}
				</SubmitButton>
				<Button theme="white"
						onClick={onCancel}>
					{t("form:buttonTexts.cancel")}
				</Button>
			</SidebarFooter>
		</form>);
};
